import React, { useState } from 'react';

import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Checkbox,
  Collapse,
  Group,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconChevronDown, IconChevronUp, IconFileText } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

import { formatStepEstimate, OnboardingStep } from './steps';
import { CHECKBOX_RADIUS, COMPLETION_GREEN, TIME_ESTIMATE_COLOR } from './tokens';

interface OnboardingChecklistCardProps {
  step: OnboardingStep;
  checked: boolean;
  isSaving: boolean;
  onToggle: (checked: boolean) => void;
}

/**
 * One step of the checklist.
 *
 * Collapsing is local to the card and independent of whether it is checked. Checking a step is
 * a claim about the gym, collapsing is about how much of the page you want to read.
 */
const OnboardingChecklistCard = ({
  step,
  checked,
  isSaving,
  onToggle,
}: OnboardingChecklistCardProps): JSX.Element => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(true);

  const primaryPath = step.destinations[0]?.path;

  return (
    <Paper
      withBorder
      radius='md'
      p='md'
      data-cy={`onboardingStep-${step.key}`}
      sx={checked ? { borderColor: COMPLETION_GREEN } : undefined}
    >
      <Group position='apart' align='flex-start' noWrap>
        <Group align='flex-start' spacing='sm' noWrap>
          <Checkbox
            size='md'
            radius={CHECKBOX_RADIUS}
            checked={checked}
            disabled={isSaving}
            onChange={event => onToggle(event.currentTarget.checked)}
            aria-label={step.title}
            data-cy={`onboardingStepCheckbox-${step.key}`}
            styles={{
              input: {
                '&:checked': { backgroundColor: COMPLETION_GREEN, borderColor: COMPLETION_GREEN },
              },
            }}
          />
          <Stack spacing={4}>
            <Group spacing='xs' align='center'>
              <Title
                order={4}
                size='h5'
                td={checked ? 'line-through' : undefined}
                color={checked ? 'dimmed' : undefined}
              >
                {step.title}
              </Title>
              {step.startHere && !checked && (
                <Badge color='blue' variant='light' tt='none'>
                  Start here
                </Badge>
              )}
              {checked && (
                <Badge variant='filled' tt='none' sx={{ backgroundColor: COMPLETION_GREEN }}>
                  Completed
                </Badge>
              )}
            </Group>
            <Text size='xs' fw={600} sx={{ color: TIME_ESTIMATE_COLOR }}>
              {formatStepEstimate(step)}
            </Text>
          </Stack>
        </Group>

        <ActionIcon
          variant='subtle'
          aria-label={isOpen ? `Collapse ${step.title}` : `Expand ${step.title}`}
          onClick={() => setIsOpen(open => !open)}
          data-cy={`onboardingStepChevron-${step.key}`}
        >
          {isOpen ? <IconChevronUp size={18} /> : <IconChevronDown size={18} />}
        </ActionIcon>
      </Group>

      <Collapse in={isOpen}>
        <Stack spacing='md' mt='md'>
          <Text size='sm' color='dimmed'>
            {step.why}
          </Text>

          <Stack spacing='xs'>
            {step.instructions.map((instruction, index) => (
              <Group key={instruction} spacing='sm' align='flex-start' noWrap>
                <Badge size='sm' radius='xl' variant='light' color='gray' sx={{ flexShrink: 0 }}>
                  {index + 1}
                </Badge>
                {index === 0 && primaryPath ? (
                  <Anchor component='button' type='button' size='sm' onClick={() => navigate(primaryPath)}>
                    {instruction}
                  </Anchor>
                ) : (
                  <Text size='sm'>{instruction}</Text>
                )}
              </Group>
            ))}
          </Stack>

          {!!step.note && (
            <Paper radius='sm' p='sm' bg='gray.0'>
              <Text size='sm' color='dimmed'>
                {step.note}
              </Text>
            </Paper>
          )}

          {step.destinations.length > 0 && (
            <Group spacing='xs'>
              {step.destinations.map(destination => (
                <Button key={destination.path} size='xs' onClick={() => navigate(destination.path)}>
                  {destination.label}
                </Button>
              ))}
            </Group>
          )}

          <Group spacing='xs'>
            <IconFileText size={16} />
            <Text size='sm' color='dimmed'>
              Help article:
            </Text>
            {step.helpArticles.map(article => (
              <Anchor key={article.url} href={article.url} target='_blank' rel='noreferrer' size='sm'>
                {article.label}
              </Anchor>
            ))}
          </Group>
        </Stack>
      </Collapse>
    </Paper>
  );
};

export default OnboardingChecklistCard;
